import * as React from 'react';
import { useStyles, useId, noop } from '@sravni/react-utils';
import { CONTAINER_SIZES } from '../../../constants/sizes.js';
import { Badge } from '../../Badge/src/Badge.js';
import { Space } from '../../Space/src/Space.js';
import modules_80570563 from '../styles/RadioButton.module.less.js';

/**
 * @description The RadioButton is a simple way to create button groups in most use cases
 *
 * @component
 * @example
 * ```jsx
 * <RadioButton options={options} defaultValue={value}>
 * ```
 */
const RadioButton = React.forwardRef((props, ref) => {
    const { options, value, defaultValue, size, border, className, onChange, ...restProps } = props;
    const cx = useStyles(modules_80570563);
    const id = useId('radio-button');
    const isFirstRender = React.useRef(true);
    const [innerValue, setInnerValue] = React.useState(value || defaultValue);
    const classNames = cx(className, 'container', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-border': border,
    });
    const handleSelect = React.useCallback((value) => () => {
        setInnerValue(value);
        onChange?.(value);
    }, [onChange]);
    const buttons = React.useMemo(() => {
        if (options && Array.isArray(options)) {
            return options.map(({ label, value, disabled, badgeText }) => {
                const classNames = cx('button', {
                    [`size-${size}`]: size,
                    active: innerValue === value,
                    disabled,
                });
                return (React.createElement("button", { key: `${id}-${value}`, type: "button", className: classNames, onClick: disabled ? noop : handleSelect(value) },
                    React.createElement(Space, { size: 4, justify: "center" },
                        label,
                        badgeText && React.createElement(Badge, { text: badgeText }))));
            });
        }
        return null;
    }, [options, cx, size, innerValue, id, handleSelect]);
    React.useEffect(() => {
        if (!isFirstRender.current) {
            setInnerValue(value);
            return;
        }
        isFirstRender.current = false;
    }, [value]);
    return (React.createElement("div", { ref: ref, "data-qa": "RadioButton", className: classNames, ...restProps }, buttons));
});
RadioButton.defaultProps = {
    size: CONTAINER_SIZES.S36,
    border: false,
};
RadioButton.displayName = 'RadioButton';

export { RadioButton };
//# sourceMappingURL=RadioButton.js.map
